import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authLogin, authVerify2fa, setAdminToken, authResendOtp } from "@/lib/api";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
};

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, refresh } = useAuth();

  const [stage, setStage] = useState("creds"); // 'creds' | 'otp' | 'done'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [phoneHint, setPhoneHint] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [smsSent, setSmsSent] = useState(true);
  const [smsError, setSmsError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailHint, setEmailHint] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendsRemaining, setResendsRemaining] = useState(null);
  const [resending, setResending] = useState(false);
  const [recoveryMsg, setRecoveryMsg] = useState(null);

  const next = new URLSearchParams(location.search).get("next") || "/admin";

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/admin/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitCreds = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await authLogin(email.trim(), password);
      setChallengeId(r.challenge_id);
      setPhoneHint(r.phone_hint || "");
      setEmailHint(r.email_hint || "");
      setSmsSent(!!r.sms_sent);
      setEmailSent(!!r.email_sent);
      setSmsError(r.sms_error || null);
      setResendCooldown(20); // match backend default
      setResendsRemaining(3);
      const parts = [];
      if (r.sms_sent) parts.push(`texted ${r.phone_hint || "your phone"}`);
      if (r.email_sent) parts.push(`emailed ${r.email_hint || "your inbox"}`);
      setInfo(parts.length
        ? `Code sent — we ${parts.join(" AND ")}.`
        : `We couldn't send the code just now${r.sms_error ? ` (${r.sms_error})` : ""}. Tap "Resend code" or use "Email me a recovery link" below.`);
      setStage("otp");
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  // Countdown effect for the resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendCooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async () => {
    if (!challengeId || resendCooldown > 0 || resending) return;
    setResending(true); setError(null);
    try {
      const r = await authResendOtp(challengeId);
      setSmsSent(!!r.sms_sent);
      setSmsError(r.sms_error || null);
      setResendCooldown(r.cooldown_seconds || 20);
      setResendsRemaining(r.resends_remaining);
      setInfo(r.sms_sent
        ? `We just sent a new code to ${r.phone_hint || phoneHint}. Older codes stop working.`
        : `Resend attempted but SMS didn't go through${r.sms_error ? ` (${r.sms_error})` : ""}. ${r.resends_remaining > 0 ? "Try again in a moment." : "Please start over."}`);
      setCode("");
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setResending(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await authVerify2fa(challengeId, code.trim());
      setAdminToken(r.token);
      setUser(r.user);
      await refresh();
      setStage("done");
      navigate(next, { replace: true });
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageWrap} data-testid="admin-login-page">
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
          NEARNOW · OWNER CONSOLE
        </div>
        <h1 style={h1}>
          {stage === "creds" ? "ADMIN SIGN-IN" : "TWO-FACTOR CHECK"}
        </h1>

        {stage === "creds" && (
          <form onSubmit={submitCreds}>
            <Label>EMAIL</Label>
            <input
              type="email"
              autoFocus
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              style={inputStyle}
              placeholder="ajakaiye@gmail.com"
            />
            <Label>PASSWORD</Label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              style={inputStyle}
            />
            {error && <p data-testid="login-error" style={errBox}>{error}</p>}
            <button
              type="submit"
              disabled={busy || !email || !password}
              data-testid="login-submit-button"
              style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Sending code…" : "SEND 2FA CODE →"}
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: "#e5e7ee" }} />
              <span style={{ color: "#9ca3af", fontSize: 11, letterSpacing: 2, fontWeight: 700 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: "#e5e7ee" }} />
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              data-testid="login-google-button"
              style={btnGoogle}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 10, verticalAlign: "middle" }}>
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14-5.5l-6.5-5.5c-2 1.5-4.5 2.3-7.5 2.3-5.2 0-9.6-3.3-11.2-8H6.2C9.6 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2 3.9-3.8 5.2l6.5 5.5C42.5 34.6 44 29.6 44 24c0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              CONTINUE WITH GOOGLE
            </button>

            <div style={{ textAlign: "center", marginTop: 14 }}>
              <a href="/admin/forgot-password" data-testid="login-forgot-link"
                 style={{ color: NAVY, fontSize: 13, textDecoration: "none", fontWeight: 700, letterSpacing: 1 }}>
                Forgot your password?
              </a>
            </div>
          </form>
        )}

        {stage === "otp" && (
          <form onSubmit={submitOtp}>
            <p style={{ color: "#5b5e69", margin: "0 0 18px" }}>
              {info || `Enter the 6-digit code sent to ${phoneHint}.`}
            </p>
            <Label>6-DIGIT CODE</Label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="login-otp-input"
              style={{ ...inputStyle, letterSpacing: 14, fontSize: 28, textAlign: "center", fontWeight: 700 }}
              placeholder="000000"
            />
            {error && <p data-testid="login-error" style={errBox}>{error}</p>}
            {!smsSent && smsError && (
              <p data-testid="login-sms-error" style={{ ...errBox, background: "#FFF5E5", color: "#8a4b00" }}>
                ⚠ SMS didn&apos;t go through — {smsError}. Use the resend button below.
              </p>
            )}
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="login-verify-button"
              style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Verifying…" : "VERIFY & SIGN IN →"}
            </button>
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <button
                type="button"
                onClick={resend}
                disabled={resendCooldown > 0 || resending || resendsRemaining === 0}
                data-testid="login-resend-button"
                style={{
                  background: "transparent",
                  color: (resendCooldown > 0 || resendsRemaining === 0) ? "#9ca3af" : NAVY,
                  border: `1.5px solid ${(resendCooldown > 0 || resendsRemaining === 0) ? "#d0d3da" : NAVY}`,
                  padding: "10px 18px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5,
                  fontSize: 12, cursor: (resendCooldown > 0 || resendsRemaining === 0) ? "not-allowed" : "pointer",
                  fontFamily: "Bebas Neue, Arial, sans-serif",
                }}>
                {resending
                  ? "Resending…"
                  : resendCooldown > 0
                    ? `RESEND CODE IN ${resendCooldown}s`
                    : resendsRemaining === 0
                      ? "NO RESENDS LEFT · START OVER"
                      : "📱 RESEND CODE"}
              </button>
              {resendsRemaining !== null && resendsRemaining > 0 && resendCooldown === 0 && (
                <div data-testid="login-resend-remaining" style={{ marginTop: 8, fontSize: 11, color: "#5b5e69", letterSpacing: 1 }}>
                  {resendsRemaining} RESEND{resendsRemaining === 1 ? "" : "S"} REMAINING
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setStage("creds"); setCode(""); setError(null); }}
              data-testid="login-back-button"
              style={btnGhost}>
              ← Back
            </button>

            {/* Owner emergency recovery — belt & suspenders when both SMS and email OTP fail */}
            <div style={{ marginTop: 16, padding: 14, background: "#F8F9FA", border: "1px dashed #d0d3da", borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: 11, letterSpacing: 2, fontWeight: 700, color: NAVY, marginBottom: 6 }}>
                CAN&apos;T GET THE CODE?
              </div>
              <button
                type="button"
                data-testid="login-recovery-button"
                disabled={recoveryMsg?.state === "sending"}
                onClick={async () => {
                  setRecoveryMsg({ state: "sending" });
                  try {
                    const r = await api.post("/auth/owner-recovery/request", { email: email.trim() }).then((x) => x.data);
                    setRecoveryMsg({ state: "sent", text: r.message || "If that email is the owner, a recovery link is on the way." });
                  } catch (err) {
                    setRecoveryMsg({ state: "sent", text: "If that email is the owner, a recovery link is on the way." });
                  }
                }}
                style={{ background: "transparent", color: NAVY, border: `1.5px solid ${NAVY}`,
                          padding: "8px 14px", borderRadius: 8, fontWeight: 700, letterSpacing: 1,
                          fontSize: 11, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" }}>
                📧 EMAIL ME A RECOVERY LINK (OWNER)
              </button>
              {recoveryMsg && (
                <div data-testid="login-recovery-msg" style={{ marginTop: 8, fontSize: 12, color: "#5b5e69" }}>
                  {recoveryMsg.state === "sending" ? "Sending…" : recoveryMsg.text}
                </div>
              )}
            </div>
          </form>
        )}

        <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 28, textAlign: "center" }}>
          Authorized personnel only. Brute-force attempts will lock the account for 15 minutes.
        </p>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{children}</div>
);

const pageWrap = { background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const card = { background: "#fff", borderRadius: 18, padding: "40px 36px", maxWidth: 460, width: "100%", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 42, color: NAVY, margin: "4px 0 22px" };
const inputStyle = { width: "100%", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, marginBottom: 18, outline: "none", boxSizing: "border-box" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 4 };
const btnGhost = { width: "100%", background: "transparent", color: NAVY, border: "none", padding: "10px", marginTop: 8, cursor: "pointer", fontSize: 13 };
const btnGoogle = { width: "100%", background: "#fff", color: NAVY, border: "1.5px solid #d0d3da", padding: "14px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 1.5, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
const errBox = { color: "#A32D2D", background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
